import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  generateReport,
  getLatestReport,
  ReportDetail,
} from '~/lib/network/funnel-report';

// Hook to generate a new report
export function useGenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      connectionId,
      periodDays = 90,
    }: {
      connectionId: string;
      periodDays?: number;
    }) => generateReport(connectionId, periodDays),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

// Hook to get latest report for a connection
// Polls every 5 seconds until we have a COMPLETED report
export function useLatestReport(
  connectionId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ReportDetail | null>({
    queryKey: ['latestReport', connectionId],
    queryFn: async () => {
      try {
        return await getLatestReport(connectionId!);
      } catch (error: any) {
        // Return null if no report exists (404) - keep polling
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!connectionId && (options?.enabled ?? true),
    retry: false,
    refetchInterval: (query) => {
      // Keep polling until we have a COMPLETED report
      const status = query.state.data?.status;
      return status === 'COMPLETED' ? false : 5000;
    },
  });
}
